import { CloseOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, Input, Switch } from 'antd'
import type { InvoiceConfigurationComponentProps as InvoiceContactsProps } from '../../../../types'
import type { ContactWithKey } from '../types'
import { BooleanSwitch } from '@/components/_SiteWide/Form/BooleanSwitch'

export function InvoiceContacts({ form }: InvoiceContactsProps) {
  const emailsEnabled = Form.useWatch('send_emails', form)
  // Handle adding a new contact
  const handleAdd = () => {
    const currentContacts = form.getFieldValue('recipients') || []
    // Create a new contact with a unique ID
    const newContact: ContactWithKey = {
      name: '',
      email: '',
      uniqueId: `new-${Date.now()}`,
    }

    form.setFieldValue('recipients', [...currentContacts, newContact])
  }

  // Maximum of 5 contacts allowed
  const contacts = Form.useWatch('recipients', form) || []
  const isMaxContacts = contacts.length >= 5

  return (
    <Vertical style={{ maxWidth: 600 }}>
      <Texto category='h5' weight='600' className='mb-3'>
        Billing Configuration
      </Texto>
      <Vertical bg='elevated' border='all' borderRadius='4px' className='py-2 px-3'>
        <Horizontal justifyContent='space-between' verticalCenter className='mb-2'>
          <Texto category='p2'>Email Invoices</Texto>
          <Form.Item name='send_emails' style={{ margin: 0 }}>
            <Switch />
          </Form.Item>
        </Horizontal>
        <Horizontal justifyContent='space-between' verticalCenter className='mb-2'>
          <Texto category='p2'>Send invoices to:</Texto>
          <GraviButton size='small' onClick={handleAdd} disabled={isMaxContacts || !emailsEnabled}>
            + Add
          </GraviButton>
        </Horizontal>
        <Form.List
          name='recipients'
          rules={[
            {
              validator: async (_, recipients) => {
                if (!recipients?.length && form.getFieldValue('send_emails') === true) {
                  return Promise.reject(new Error('At least one recipient is required when emailing invoices'))
                }
              },
            },
          ]}
        >
          {(fields, { remove }, { errors }) => (
            <Vertical border='top' className='px-2 py-3' gap={8}>
              {fields.map((field) => (
                <Horizontal key={field.key} verticalCenter gap={8}>
                  <Form.Item {...field} name={[field.name, 'name']} style={{ margin: 0, flex: 1 }}>
                    <Input placeholder='Name' disabled={!emailsEnabled} />
                  </Form.Item>
                  <Form.Item
                    {...field}
                    name={[field.name, 'email']}
                    style={{ margin: 0, flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please input an email address',
                      },
                      {
                        type: 'email',
                        message: 'Please enter a valid email address',
                      },
                    ]}
                  >
                    <Input placeholder='Email' type='email' disabled={!emailsEnabled} />
                  </Form.Item>
                  <GraviButton
                    appearance='link'
                    size='small'
                    color='danger'
                    icon={<CloseOutlined />}
                    onClick={() => remove(field.name)}
                    disabled={!emailsEnabled}
                  />
                </Horizontal>
              ))}
              <Form.ErrorList className='error-message' errors={errors} />
              {isMaxContacts && <Texto category='p2'>Maximum of 5 contacts reached</Texto>}
            </Vertical>
          )}
        </Form.List>
        <Texto category='p2' className='mb-3'>
          Email Attachments
        </Texto>
        <Horizontal gap={32} className='px-2 mb-2'>
          <Vertical flex={'none'} gap={'0.5rem'}>
            <BooleanSwitch
              disabled={!emailsEnabled}
              title='Invoice PDF'
              name='include_invoice_pdf'
              initialValue={true}
            />
            <BooleanSwitch
              disabled={!emailsEnabled}
              title='Invoice Excel'
              name='include_invoice_excel'
              initialValue={true}
            />
          </Vertical>
          <Vertical flex={'none'} gap={'0.5rem'}>
            <BooleanSwitch
              disabled={!emailsEnabled}
              title='Include BOL Images'
              name='include_bol_images'
              initialValue={false}
            />
            <BooleanSwitch
              disabled={!emailsEnabled}
              title='Include Delivery Tickets'
              name='include_delivery_tickets'
              initialValue={false}
            />
          </Vertical>
        </Horizontal>
      </Vertical>
    </Vertical>
  )
}
